import { useCallback, useMemo } from 'react';

import { useLocalStorageState } from '@components/component-helpers';

import { DateFilterOption, GroupByOption, PortalStatisticsFilters } from 'api/portals/requests';

type DateRange = {
  startDate?: string;
  endDate?: string;
};

export const usePortalFilters = () => {
  const [selectedPortalIds, setSelectedPortalIds] = useLocalStorageState<string[]>({
    key: 'portal-statistics-selected-portals',
    defaultValue: [],
  });

  const [selectedDateFilter, setSelectedDateFilter] = useLocalStorageState<DateFilterOption>({
    key: 'portal-statistics-date-filter',
    defaultValue: 'last30Days',
  });
  const [selectedGroupBy, setSelectedGroupBy] = useLocalStorageState<GroupByOption>({
    key: 'portal-statistics-group-by',
    defaultValue: 'week',
  });

  const [customDateRange, setCustomDateRange] = useLocalStorageState<DateRange>({
    key: 'portal-statistics-custom-date-range',
    defaultValue: {},
  });

  const handleDateFilterChange = useCallback(
    (dateFilter: DateFilterOption) => {
      setSelectedDateFilter(dateFilter);
    },
    [setSelectedDateFilter]
  );

  const handleCustomDateRangeChange = useCallback(
    (newCustomDateRange: DateRange) => {
      setCustomDateRange(newCustomDateRange);
    },
    [setCustomDateRange]
  );

  const removeSelectedPortal = useCallback(
    (shortId: string) => {
      setSelectedPortalIds(selectedPortalIds.filter((id) => id !== shortId));
    },
    [selectedPortalIds, setSelectedPortalIds]
  );

  const statisticsFilters: PortalStatisticsFilters = useMemo(
    () => ({
      portalShortIds: selectedPortalIds.length > 0 ? selectedPortalIds : undefined,
      dateFilter: selectedDateFilter,
      groupBy: selectedGroupBy,
      ...(selectedDateFilter === 'dateToNow' &&
        customDateRange.startDate && {
          startDate: customDateRange.startDate,
        }),
      ...(selectedDateFilter === 'chooseADateRange' &&
        customDateRange.startDate &&
        customDateRange.endDate && {
          startDate: customDateRange.startDate,
          endDate: customDateRange.endDate,
        }),
    }),
    [selectedPortalIds, selectedDateFilter, selectedGroupBy, customDateRange]
  );

  return {
    selectedPortalIds,
    setSelectedPortalIds,
    selectedDateFilter,
    selectedGroupBy,
    setSelectedGroupBy,
    customDateRange,
    setCustomDateRange,
    handleDateFilterChange,
    handleCustomDateRangeChange,
    removeSelectedPortal,
    statisticsFilters,
  };
};
